<template>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <!-- 헤더 -->
          <DetailHeader :headerTitle="headerTitle" :useBackBtn="!myPage" :backTo="'Users'"/>

          <div class="pt-3 pb-4 d-flex justify-content-center">
            <div v-if="isEdit && loading">
              <TableBodyLoader :minHeight="715" :offsetY="-50" message="상세정보를 불러오는 중..." />
            </div>
            <div v-else class="w-100 px-5">  
              <section class="card mb-4 border-0">
                <div class="card-header pb-0">
                  <h6 class="mb-0">{{ isEdit ? '비밀번호 변경' : '로그인 정보' }}</h6>
                </div>
                <div class="card-body">

                  <div class="row g-3">
                    <!-- 공통: 아이디 -->
                    <div class="col-md-6">
                      <label class="form-label">아이디 <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="form.emplyrId"
                        :disabled="isEdit"
                        placeholder="아이디를 입력하세요"
                      />
                    </div>

                    <div class="col-md-6"></div>

                    <!-- ✅ 수정 모드: 새 비밀번호/확인 -->
                    <template v-if="isEdit">
                      <div class="col-md-6">
                        <label class="form-label">새 비밀번호</label>
                        <input type="password" class="form-control" v-model="security.next" autocomplete="new-password" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">새 비밀번호 확인</label>
                        <input type="password" class="form-control" v-model="security.nextConfirm" autocomplete="new-password" />
                      </div>
                    </template>

                    <!-- ✅ 등록 모드: 최초 비밀번호/확인 -->
                    <template v-else>
                      <div class="col-md-6">
                        <label class="form-label">비밀번호 <span class="text-danger">*</span></label>
                        <input type="password" class="form-control" v-model="form.password" autocomplete="new-password" placeholder="비밀번호" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label">비밀번호 확인 <span class="text-danger">*</span></label>
                        <input type="password" class="form-control" v-model="form.passwordCnf" autocomplete="new-password" placeholder="비밀번호 확인" />
                      </div>
                    </template>
                  </div>

                  <div class="d-flex justify-content-end mt-4">
                    <soft-button v-if="isEdit" color="success" @click="chgPasswd">변경</soft-button>
                  </div>
                </div>
              </section>

              <!-- 프로필 -->
              <section class="card mb-5 border-0">
                <div class="card-header pb-0">
                  <h6 class="mb-0">사용자 정보</h6>
                </div>
                <div class="card-body">
                  <div class="d-flex align-items-center gap-3 mb-4">
                      <ImagePicker
                        v-model:file="form.imgFile"
                        :existing-preview="form.imgPreview"
                        :existing-name="form.imgFilename"
                        :max-size="10 * 1024 * 1024"
                        label="대표 이미지"
                        alt="workplace"
                        @selected="onImgSelected"
                        @cleared="onImgCleared"
                        @error="toastError"
                      />
                  </div>

                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">이름 <span class="text-danger">*</span></label>
                      <input type="text" class="form-control" v-model.trim="form.userNm" placeholder="홍길동" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">직급</label>
                      <input type="text" class="form-control" v-model.trim="form.rankNm" placeholder="매니저" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">회사코드</label>
                      <input type="text" class="form-control" v-model.trim="form.mfId" placeholder="회사명" readonly/>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">부서</label>
                      <select class="form-select" v-model.trim="form.orgnztId">
                        <option value="">선택</option>
                        <option 
                          v-for="org in orgnzList" 
                          :key="org.orgnztId" 
                          :value="org.orgnztId"
                        >
                          {{ org.orgnztNm }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">권한 그룹</label>
                      <select  v-if="!myPage" class="form-select" v-model="form.groupId">
                        <option value="">선택</option>
                        <option 
                          v-for="group in authGroupList" 
                          :key="group.groupId" 
                          :value="group.groupId"
                        >
                          {{ group.groupDc }}
                        </option>
                      </select>
                      <input v-else type="text" class="form-control" :value="userInfo?.groupDc" readonly/>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">상태</label>
                      <select class="form-select" v-model="form.emplyrSttusCode">
                        <option value="">선택</option>
                        <option value="P">정상</option>
                        <option value="S">중지</option>
                        <option value="D">탈퇴</option>
                      </select>
                    </div>
                    <div class="col-md-12">
                      <label class="form-label">비고</label>
                      <textarea class="form-control" rows="3" v-model.trim="form.dsc" placeholder="비고를 입력하세요."></textarea>
                    </div>
                  </div>

                  <!-- 메타 -->
                  <div class="row g-3 mt-1" v-if="isEdit">
                    <div class="col-md-6">
                      <label class="form-label">생성일시</label>
                      <input type="text" class="form-control" :value="formatDate(form.sbscrbDe) || '-'" disabled />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">수정일시</label>
                      <input type="text" class="form-control" :value="formatDate(form.updatedAt) || '-'" disabled />
                    </div>
                  </div>
                </div>
              </section>

              <!-- 액션 -->
              <div v-if="!myPage" class="d-flex justify-content-between mt-5 mb-3 gap-2 px-2">
                <soft-button
                  color="outline-secondary"
                  @click="$router.push({ name: 'Users', query: { ...$route.query } })"
                >
                  목록
                </soft-button>
                <div class="d-flex gap-2">
                  <soft-button v-if="isEdit" color="danger" @click="remove" :disabled="loading">삭제</soft-button>
                  <soft-button color="success" @click="save" :disabled="loading">저장</soft-button>
                </div>
              </div>
              <div v-else class="d-flex justify-content-end mt-5 mb-3 gap-2 px-2">
                <soft-button color="success" @click="save" :disabled="loading">저장</soft-button>
              </div>

              <!-- 알림 설정 -->
              <!-- <section class="card mb-2 border-0">
                <div class="card-header pb-0">
                  <h6 class="mb-0">알림 설정</h6>
                </div>
                <div class="card-body">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" id="emailNoti" v-model="notify.email" />
                        <label class="form-check-label" for="emailNoti">이메일 알림</label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" id="smsNoti" v-model="notify.sms" />
                        <label class="form-check-label" for="smsNoti">SMS 알림</label>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" id="pushNoti" v-model="notify.push" />
                        <label class="form-check-label" for="pushNoti">푸시 알림</label>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end mt-4">
                    <soft-button color="success" @click="saveNotify">저장</soft-button>
                  </div>
                </div>
              </section> -->
            </div>
            <!-- // content-wrap -->
          </div>
          <!-- // inner body -->
        </div>
      </div>
    </div>
<SoftConfirm ref="confirm" />
</template>

<script>
import SoftConfirm from "@/components/SoftConfirm.vue";
import DetailHeader from "@/components/DetailHeader.vue";
import SoftButton from "@/components/SoftButton.vue";
import TableBodyLoader from "@/components/loading/TableBodyLoader.vue";
import ImagePicker from "@/views/components/common/ImagePicker.vue";
import api from "@/api/api";

export default {
  name: "UserDetail",
  components: { SoftConfirm, DetailHeader, SoftButton, TableBodyLoader, ImagePicker },
  inject: ['showLoading', 'hideLoading'],
  props: {
    mode: { type: String, default: 'create' },
    id: { type: String, default: '' },
    myPage: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      form: {
        emplyrId: "",
        orgnztId: "",
        rankNm: "",
        userNm: "",
        password: "",
        passwordCnf: "",
        groupId: "",
        emplyrSttusCode: "",
        dsc: "",
        sbscrbDe: "",
        mfId: "",
        atchFileId: "",
        imgFile: null,
        imgPreview: "", 
        imgFilename: "",
        sbscrbDe: "",
        updatedAt: "",
        esntlId: "",
      },
      imgChanged: false,
      imgRemoved: false, 
      security: {
        next: "",
        nextConfirm: ""
      },
      authGroupList: [],
      orgnzList: [],
    };
  },
  computed: {
    isEdit() { return this.mode === 'edit' && !!this.id; },
    headerTitle() { return this.isEdit ? '사용자 수정' : '사용자 등록'; },
    displayFileName() {
      return this.form.imgFile?.name || this.form.imgFilename || "";
    },
  },
  async mounted() {
    this.form.mfId = this.$store.getters['UserStore/getMfId'];
    this.initData();
    if (this.isEdit) this.fetchDetail();
  },
  beforeUnmount() {
    if (this.form.imgPreview) URL.revokeObjectURL(this.form.imgPreview);
  },
  methods: {
    async fetchDetail() {
      this.loading = true;
      try {
        const [res] = await Promise.all([
          api.get(`/user`, { params: { id: this.id } }),
          new Promise(r => setTimeout(r, 300))
        ]);
        const item = res.data?.result?.userInfo || {};
        const files = res.data?.result?.files || [];
        Object.assign(this.form, item);
        
        if (files.length > 0) {
          this.form.imgPreview  = files[0].downloadUrl || "";
          this.form.imgFilename = files[0].originalName || "";
        } else {
          this.form.imgPreview  = "";
          this.form.imgFilename = "";
        }
        this.files = files;
      } catch (e) {
        this.toastError?.("상세 조회 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },
    async initData() {
      const mfId = this.$store.getters['UserStore/getMfId'];
      try {
        const res = await api.get(`/user/initData`, { params: { mfId: mfId } });
        this.orgnzList = res.data?.result?.orgnzList || [];
        this.authGroupList = res.data?.result?.authGroupList || [];

      } catch (e) {
        this.toastError?.("기초데이터 조회 중 오류가 발생했습니다.");
      } finally {
      }
    },
    onImgSelected({ file, previewUrl, name }) {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name;

      this.imgChanged = true;
      this.imgRemoved = false;
    },
    onImgCleared() {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = null;
      this.form.imgPreview  = "";
      this.form.imgFilename = "";

      this.imgChanged = false;
      this.imgRemoved = true;
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("emplyrId", this.form.emplyrId ?? "");
      fd.append("orgnztId", this.form.orgnztId ?? "");
      fd.append("rankNm", this.form.rankNm ?? "");
      fd.append("userNm", this.form.userNm ?? "");
      fd.append("groupId", this.form.groupId ?? "");
      fd.append("dsc", this.form.dsc ?? "");
      fd.append("emplyrSttusCode", this.form.emplyrSttusCode ?? "");
      if (!this.isEdit) {
        fd.append("password", this.form.password ?? "");
        fd.append("passwordCnf", this.form.passwordCnf ?? "");
      }
      if (this.form.email) fd.append("email", this.form.email);
      if (this.form.telNo) fd.append("telNo", this.form.telNo);
      if (this.form.memo) fd.append("memo", this.form.memo);
      if (this.form.mfId !== null && this.form.mfId !== undefined) fd.append("mfId", String(this.form.mfId));
      if (this.form.atchFileId) fd.append("atchFileId", this.form.atchFileId);

      fd.append("imgChanged", this.imgChanged ? "Y" : "N");
      fd.append("imgRemoved", this.imgRemoved ? "Y" : "N");
      if (this.form.imgFile) fd.append("file", this.form.imgFile);
      return fd;
    },
    /** 저장(등록/수정) */
    async save() {
      // 필수값
      if (!this.form.emplyrId?.trim()) { this.toastError?.("아이디를 입력하세요."); return; }
      if (!this.form.userNm?.trim()) { this.toastError?.("이름을 입력하세요."); return; }

      if (!this.isEdit) {
        if (!this.form.password?.trim() || !this.form.passwordCnf?.trim()) {
          this.toastError?.("비밀번호와 확인 비밀번호를 입력하세요."); return;
        }
        if (this.form.password !== this.form.passwordCnf) {
          this.toastError?.("비밀번호가 일치하지 않습니다."); return;
        }
      }

      const action = this.isEdit ? "수정" : "등록";
      const ok = await this.$refs.confirm.open({
        title: `사용자 ${action}`,
        message: `${action} 하시겠습니까?`,
        confirmText: action,
        cancelText: "취소",
        confirmColor: "success",
      });
      if (!ok) return;

      const delay = (ms) => new Promise(r => setTimeout(r, ms));
      const started = performance.now();

      this.showLoading?.("저장중...");
      try {
        const fd = this.buildFormData();

        let savedRes;
        if (this.isEdit) {
          const { data } = await api.put(`/user/${this.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          savedRes = data?.result || {};
        } else {
          const { data } = await api.post(`/user`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          this.form.emplyrId = data?.result?.emplyrId || this.form.emplyrId;
          savedRes = data?.result || {};
        }

        // ===========================
        // 🔥 여기부터 헤더 프로필 즉시 갱신 로직
        // ===========================
        const me = this.$store.getters['UserStore/getUserInfo'];
        const editedId = this.isEdit ? this.id : this.form.emplyrId;
        const isMyself = me && (me.id === editedId || me.emplyrId === editedId);

        if (isMyself && (this.imgChanged || this.imgRemoved)) {
          if (savedRes.downloadUrl) {
            const bust = `${savedRes.downloadUrl}`;
            this.$store.commit('UserStore/setUserImage', bust);
          } else {
            this.$store.commit('UserStore/setUserImage', null);
          }
        }
        if (isMyself) {
          this.$store.dispatch('UserStore/updateUserInfo', {
            name: this.form.userNm,
            groupId: this.form.groupId,
          });
        }
        // ===========================

        const elapsed = performance.now() - started;
        if (elapsed < 800) await delay(800 - elapsed);

        this.toastSuccess?.(`사용자 정보가 ${action}되었습니다.`);
        this.$router.push({
          name: this.myPage ? "Profile" : "UserEdit",
          params: { id: this.form.emplyrId },
          query: { ...this.$route.query, t: Date.now()},
        });
      } catch (e) {
        console.error(e);
        this.toastError?.(`${action} 중 오류가 발생했습니다.`);
      } finally {
        this.hideLoading?.();
      }
    },
    async chgPasswd() {
      if (!this.isEdit) return;
      if (!this.security.next?.trim() || !this.security.nextConfirm?.trim()) {
        this.toastError?.("새 비밀번호와 확인을 입력하세요."); return;
      }
      if (this.security.next !== this.security.nextConfirm) {
        this.toastError?.("새 비밀번호가 일치하지 않습니다."); return;
      }
      if (this.security.next.length < 4) {
        this.toastError?.("비밀번호는 4자리 이상이어야 합니다."); return;
      }

      const ok = await this.$refs.confirm.open({
        title: "비밀번호 변경",
        message: "비밀번호를 변경하시겠습니까?",
        confirmText: "변경",
        cancelText: "취소",
        confirmColor: "success",
      });
      if (!ok) return;

      const delay = (ms) => new Promise(r => setTimeout(r, ms));
      const started = performance.now();
      
      this.showLoading?.("저장중...");
      try {
        await api.put(`/user/${this.id}/password`, {
          password: this.security.next
        });

        const elapsed = performance.now() - started;
        if (elapsed < 800) await delay(800 - elapsed);

        this.toastSuccess?.("비밀번호가 변경되었습니다.");
        this.security.next = "";
        this.security.nextConfirm = "";
      } catch (e) {
        console.error(e);
        this.toastError?.("비밀번호 변경 중 오류가 발생했습니다.");
      } finally {
        this.hideLoading?.();
      }
    },
    /** 삭제 */
    async remove() {
      if (!this.isEdit) return;

      const ok = await this.$refs.confirm.open({
        title: "삭제 확인",
        message: "정말 삭제하시겠습니까?",
        confirmText: "삭제",
        cancelText: "취소",
        confirmColor: "danger",
      });
      if (!ok) return;

      this.showLoading?.("삭제중...");
      try {
        const delay = ms => new Promise(r => setTimeout(r, ms));
        await Promise.all([
          api.delete(`/user/${this.id}`),
          delay(500),
        ]);

        this.toastSuccess?.("삭제되었습니다.");
        this.$router.push({
          name: "Users",
          query: { ...this.$route.query },
        });
      } catch (e) {
        console.error(e);
        this.toastError?.("삭제 중 오류가 발생했습니다.");
      } finally {
        this.hideLoading?.();
      }
    }
  },
};
</script>

<style scoped>
.card { border: 1px solid rgba(0,0,0,.06) !important; }

</style>